import { useQuery } from '@tanstack/react-query';
import { agentPermissions } from '@/api/agentPermissions';
import { useAuth } from '@/lib/auth-store';

const QUERY_KEY = ['my-module-permissions'] as const;
const STALE_MS = 60_000;

/**
 * Fetches the signed-in user's OWN permission matrix (agents + limited users
 * only — every other role is never matrix-governed, so no request is made)
 * and returns a `canReadScope(scope)` helper for nav filtering.
 *
 * Semantics mirror the backend (`requireModulePermission`):
 *   - non-governed roles → always true
 *   - governed roles     → true unless the matrix EXPLICITLY sets
 *                          `<scope>.read = false` (absent = allowed)
 *   - while loading      → true (avoid a nav flash; the API is the real gate)
 */
export function useMyModulePermissions() {
  const user = useAuth((s) => s.user);
  const governed = user?.role === 'agent' || user?.role === 'limited_user';

  const query = useQuery({
    queryKey: [...QUERY_KEY, user?.id ?? 'anon'],
    queryFn: () => agentPermissions.me(),
    staleTime: STALE_MS,
    enabled: Boolean(user) && governed,
  });

  function canReadScope(scope: string): boolean {
    if (!governed) return true;
    if (!query.data) return true; // loading / error → fail open, API enforces
    const scopeObj = query.data.permissions?.[scope];
    return scopeObj?.read !== false;
  }

  return {
    canReadScope,
    permissions: query.data?.permissions ?? {},
    isLoading: query.isLoading,
    /** True when the current role's nav is matrix-governed at all. */
    governed,
  };
}

export { QUERY_KEY as MY_MODULE_PERMISSIONS_QUERY_KEY };
